// ============================================
// THERMAL PRINTER TEMPLATE DESIGNS
// 10 Exceptional Market-Standard Templates
// ============================================

// ============================================
// SHARED GST CALCULATION HELPER
// Combines ALL GST groups into single rows (one for inclusive, one for exclusive)
// ============================================
const calculateGSTDetails = (items) => {
    // Separate calculations for inclusive and exclusive taxes - COMBINED into single row each
    const inclusiveDetails = {
        taxableValue: 0,
        totalCGST: 0,
        totalSGST: 0,
        totalGST: 0
    };
    
    const exclusiveDetails = {
        taxableValue: 0,
        totalCGST: 0,
        totalSGST: 0,
        totalGST: 0
    };

    items.forEach(item => {
        if (item.gstGroup) {
            const isInclusive = item.gstGroup.type === 'inclusive';
            const itemTaxableValue = isInclusive 
                ? item.total - (item.tax || 0)
                : item.total;
            
            const cgstAmount = (itemTaxableValue * (item.gstGroup.cgst || 0)) / 100;
            const sgstAmount = (itemTaxableValue * (item.gstGroup.sgst || 0)) / 100;
            
            if (isInclusive) {
                inclusiveDetails.taxableValue += itemTaxableValue;
                inclusiveDetails.totalCGST += cgstAmount;
                inclusiveDetails.totalSGST += sgstAmount;
                inclusiveDetails.totalGST += item.tax || 0;
            } else {
                exclusiveDetails.taxableValue += itemTaxableValue;
                exclusiveDetails.totalCGST += cgstAmount;
                exclusiveDetails.totalSGST += sgstAmount;
                exclusiveDetails.totalGST += item.tax || 0;
            }
        }
    });

    return { inclusiveDetails, exclusiveDetails };
};

// ============================================
// CALCULATE TOTAL SAVINGS
// ============================================
const calculateTotalSavings = (items) => {
    let totalSavings = 0;
    
    items.forEach(item => {
        if (item.selectedOption) {
            const originalPrice = item.selectedOption.originalPrice || 0;
            const discountPrice = item.selectedOption.discountPrice || originalPrice;
            const savingsPerUnit = originalPrice - discountPrice;
            totalSavings += savingsPerUnit * item.quantity;
        }
    });
    
    return totalSavings;
};

// Sample data for preview
const SAMPLE_DATA = {
    invoiceNumber: 'INV-2024-00123',
    orderPlacedOn: new Date().toISOString(),
    salesPerson: 'John Doe',
    items: [
        {
            productName: 'Premium Rice (Basmati)',
            selectedOption: { weight: '1 KG', originalPrice: 180.00, discountPrice: 150.00 },
            quantity: 2,
            price: 150.00,
            total: 300.00,
            tax: 15.00,
            hsnCode: '1006',
            gstGroup: { cgst: 2.5, sgst: 2.5, percentage: 5, type: 'inclusive' }
        },
        {
            productName: 'Toor Dal',
            selectedOption: { weight: '500 GM', originalPrice: 100.00, discountPrice: 85.00 },
            quantity: 1,
            price: 85.00,
            total: 85.00,
            tax: 4.25,
            hsnCode: '0713',
            gstGroup: { cgst: 2.5, sgst: 2.5, percentage: 5, type: 'inclusive' }
        },
        {
            productName: 'Sugar (Premium Quality)',
            selectedOption: { weight: '1 KG', originalPrice: 50.00, discountPrice: 45.00 },
            quantity: 1,
            price: 45.00,
            total: 45.00,
            tax: 2.25,
            hsnCode: '1701',
            gstGroup: { cgst: 2.5, sgst: 2.5, percentage: 5, type: 'inclusive' }
        }
    ],
    financial: {
        subtotal: 430.00,
        taxInclusive: 21.50,
        taxExclusive: 0,
        finalAmount: 430.00
    }
};

const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const formatIndianNumber = (num) => {
    return parseFloat(num).toFixed(2);
};

// ============================================
// SIZE SCALING HELPERS
// Keep 80mm styles unchanged, progressively scale down for 58/50/40mm
// ============================================
const scaleForWidth = (width) => {
    const w = parseInt(width) || 80;
    if (w >= 80) return 1.0;      // keep original sizes for 80mm
    if (w >= 58) return 0.88;     // smaller for 58mm
    if (w >= 50) return 0.80;     // more compact for 50mm
    return 0.70;                  // very compact for 40mm and below
};

const scaled = (pxValue, width) => {
    // Accept strings like '11px' or numbers
    const raw = (typeof pxValue === 'string' && pxValue.endsWith('px')) ? parseFloat(pxValue) : Number(pxValue);
    const scale = scaleForWidth(width);
    const scaledVal = Math.max(6, Math.round(raw * scale));
    return `${scaledVal}px`;
};

// Scale padding/margin values for smaller widths
const scaledSpace = (pxValue, width) => {
    const raw = (typeof pxValue === 'string' && pxValue.endsWith('px')) ? parseFloat(pxValue) : Number(pxValue);
    const w = parseInt(width) || 80;
    let scale = 1.0;
    if (w >= 80) scale = 1.0;
    else if (w >= 58) scale = 0.75;
    else if (w >= 50) scale = 0.6;
    else scale = 0.5;
    const scaledVal = Math.max(1, Math.round(raw * scale));
    return `${scaledVal}px`;
};

// ============================================
// TEMPLATE 1: CLASSIC PROFESSIONAL
// ============================================
const classicTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    
    const gst = calculateGSTDetails(items);
    
    const itemsHTML = items.map(item => {
        return `
            <tr>
                <td style="font-size: ${scaled('10.5px', width)}; padding: ${scaledSpace('3px', width)} 0; max-width: ${width >= 80 ? '35mm' : width >= 58 ? '28mm' : '22mm'}; vertical-align: top;">
                    <div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div>
                    ${item.selectedOption?.weight ? `<div style="font-size: ${scaled('9px', width)};">(${typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight})</div>` : ''}
                </td>
                <td style="font-size: ${scaled('10.5px', width)}; padding: ${scaledSpace('3px', width)} ${scaledSpace('3px', width)}; text-align: center; width: ${width >= 80 ? '12mm' : '10mm'}; vertical-align: top;">${item.quantity.toFixed(2)}</td>
                <td style="font-size: ${scaled('10.5px', width)}; padding: ${scaledSpace('3px', width)} ${scaledSpace('3px', width)}; text-align: right; width: ${width >= 80 ? '12mm' : '10mm'}; vertical-align: top;">${item.price.toFixed(2)}</td>
                <td style="font-size: ${scaled('10.5px', width)}; padding: ${scaledSpace('3px', width)} 0; text-align: right; width: ${width >= 80 ? '13mm' : '11mm'}; vertical-align: top;">${item.total.toFixed(2)}</td>
            </tr>
        `;
    }).join('');
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Invoice ${invoiceNumber}</title>
    <style>
        @page {
            size: ${width}mm auto;
            margin: 0;
        }
        @media print {
            body { margin: 0; padding: 0; }
            .no-print { display: none !important; }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.4;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .extrabold { font-weight: 800; }
        .large { font-size: ${scaled('16px', width)}; }
        .separator-thin { border-top: 0.5px solid #000; margin: ${scaledSpace('2px', width)} 0; }
        .separator { border-top: 1px solid #000; margin: ${scaledSpace('4px', width)} 0; }
        table { width: 100%; border-collapse: collapse; margin: ${scaledSpace('4px', width)} 0; }
        td { padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)}; word-wrap: break-word; }
    </style>
</head>
<body>
    <div class="center">
        <div class="large extrabold">DPBN SUPER MARTS PRIVATE LIMITED</div>
        <div class="bold" style="margin-top: ${scaledSpace('4px', width)}; font-size: ${scaled('11px', width)};">
            23-9-26/1, Nirmala Nagar, Near Railway Gate, <br />
            Sattenapalli, Palnadu, AP – 522403<br />
            Phone: 9493921579
        </div>
        <div style="margin-top: ${scaledSpace('4px', width)}; font-size: ${scaled('12px', width)};" class="bold">GSTIN: 37AALCD5749H1Z7</div>
    </div>
    
    <div class="separator"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <tr><td class="bold" style="width: 40%;">Invoice No:</td><td style="text-align: right;">${invoiceNumber}</td></tr>
        <tr><td class="bold">Date & Time:</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td class="bold">Sales Person:</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td class="bold">Payment:</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="separator-thin"></div>
    
    <table>
        <thead>
            <tr class="bold" style="font-size: ${scaled('11px', width)}; border-bottom: 1px solid #000;">
                <td style="padding: ${scaledSpace('2px', width)} 0;">ITEM</td>
                <td style="padding: ${scaledSpace('2px', width)} ${scaledSpace('2px', width)}; text-align: center; width: ${width >= 80 ? '12mm' : '10mm'};">QTY</td>
                <td style="padding: ${scaledSpace('2px', width)} ${scaledSpace('2px', width)}; text-align: right; width: ${width >= 80 ? '12mm' : '10mm'};">RATE</td>
                <td style="padding: ${scaledSpace('2px', width)} 0; text-align: right; width: ${width >= 80 ? '13mm' : '11mm'};">AMT</td>
            </tr>
        </thead>
        <tbody>
            ${itemsHTML}
        </tbody>
    </table>
    
    <div class="separator-thin"></div>
    
    <table style="font-size: ${scaled('11.5px', width)};">
        <tr><td>Subtotal:</td><td style="text-align: right;" class="bold">Rs. ${formatIndianNumber(items.reduce((sum, item) => sum + item.total, 0))}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax (Exclusive):</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="separator-thin"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <tr class="large bold">
            <td>TOTAL:</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="separator"></div>
    
    <div class="center bold" style="margin: ${scaledSpace('6px', width)} 0; text-decoration: underline; font-size: ${scaled('11px', width)};">GST BREAKDOWN</div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div style="font-size: ${scaled('10px', width)}; font-weight: bold; margin: ${scaledSpace('4px', width)} 0;">TAX (<span style="font-weight: normal; font-size: ${scaled('8px', width)};">inclusive</span>)</div>
    <table style="font-size: ${scaled('9.5px', width)};">
        <thead>
            <tr class="bold" style="border-bottom: 1px solid #000;">
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">TAX VALUE</td>
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">CGST</td>
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">SGST</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">TOTAL<br>GST</td>
            </tr>
        </thead>
        <tbody>
            <tr style="border-top: 1px solid #000; font-size: ${scaled('9px', width)};">
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td>
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td>
                <td style="text-align: center; padding: ${scaledSpace('2px', width)};">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)}; font-weight: bold;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td>
            </tr>
        </tbody>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div style="font-size: ${scaled('10px', width)}; font-weight: bold; margin: ${scaledSpace('4px', width)} 0;">TAX (<span style="font-weight: normal; font-size: ${scaled('8px', width)};">exclusive</span>)</div>
    <table style="font-size: ${scaled('9.5px', width)};">
        <thead>
            <tr class="bold" style="border-bottom: 1px solid #000;">
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">TAXABLE<br>VALUE</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">CGST</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">SGST</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">TOTAL<br>GST</td>
            </tr>
        </thead>
        <tbody>
            <tr style="border-top: 1px solid #000; font-size: ${scaled('9px', width)};">
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)};">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td>
                <td style="text-align: right; padding: ${scaledSpace('2px', width)}; font-weight: bold;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td>
            </tr>
        </tbody>
    </table>
    ` : ''}
    
    <div class="separator-thin"></div>
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="margin: ${scaledSpace('6px', width)} 0; font-size: ${scaled('11px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="separator-thin"></div>
    ` : '';
    })()}
    
    <div class="center medium bold" style="margin: ${scaledSpace('8px', width)} 0; font-size: ${scaled('11px', width)};">
        THANK YOU FOR VISITING! ❤️
    </div>
    
    <div class="center" style="font-size: ${scaled('11px', width)};">
        Customer Care: 9493921579
    </div>
    
    <div style="margin: ${scaledSpace('12px', width)} 0;"></div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 2: MODERN MINIMALIST
// ============================================
const modernTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: Arial, sans-serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.6;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 5mm;
            color: #333;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .light-sep { border-top: 0.5px solid #ddd; margin: ${scaledSpace('5px', width)} 0; }
        .dark-sep { border-top: 2px solid #333; margin: ${scaledSpace('5px', width)} 0; }
        table { width: 100%; border-collapse: collapse; margin: ${scaledSpace('4px', width)} 0; }
        td { padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)}; }
        .spacer { height: 8px; }
    </style>
</head>
<body>
    <div class="center">
        <div class="extrabold" style="font-size: ${scaled('20px', width)}; letter-spacing: 1px;">DPBN SUPER MARTS</div>
        <div class="semibold" style="margin-top: ${scaledSpace('6px', width)}; color: #666; font-size: ${scaled('10px', width)};">
            23-9-26/1, Nirmala Nagar, Sattenapalli<br/>
            Phone: 9493921579 | GSTIN: 37AALCD5749H1Z7
        </div>
    </div>
    
    <div class="dark-sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('10px', width)}; color: #666;">
        <tr><td>Invoice #</td><td style="text-align: right;" class="bold" style="color: #333;">${invoiceNumber}</td></tr>
        <tr><td>Date & Time</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td>Served by</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td>Payment</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="light-sep"></div>
    
    ${items.map(item => `
        <div style="margin: ${scaledSpace('6px', width)} 0;">
            <div class="bold" style="font-size: ${scaled('12px', width)}; color: #000; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}${item.selectedOption?.weight ? ` (${typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight})` : ''}</div>
            <table class="semibold" style="font-size: ${scaled('11px', width)}; color: #666; margin-top: ${scaledSpace('2px', width)};">
                <tr>
                    <td>${item.quantity} × Rs. ${item.price.toFixed(2)}</td>
                    <td style="text-align: right;" class="bold" style="color: #000; font-size: ${scaled('12px', width)};">Rs. ${item.total.toFixed(2)}</td>
                </tr>
            </table>
        </div>
        <div class="light-sep"></div>
    `).join('')}
    
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>Subtotal</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax (Exclusive)</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="dark-sep"></div>
    
    <table style="font-size: ${scaled('18px', width)};">
        <tr class="extrabold">
            <td>TOTAL</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="dark-sep"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center extrabold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('13px', width)};">GST BREAKDOWN</div>
    <div class="light-sep"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('3px', width)} 0;">TAX (inclusive)</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value:</td><td style="text-align: right;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total GST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('3px', width)} 0;">TAX (exclusive)</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value:</td><td style="text-align: right;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total GST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="light-sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="margin: ${scaledSpace('6px', width)} 0; font-size: ${scaled('11px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="light-sep"></div>
    ` : '';
    })()}
    
    <div class="spacer"></div>
    <div class="center bold" style="font-size: ${scaled('13px', width)};">Thank you for shopping with us!</div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 3: COMPACT SAVER
// ============================================
const compactTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: Arial, sans-serif;
            font-size: ${scaled('10px', width)};
            line-height: 1.2;
            width: ${width - 6}mm;
            margin: 0 auto;
            padding: 2mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .sep { border-top: 1px solid #000; margin: ${scaledSpace('1px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('1px', width)} 0; }
    </style>
</head>
<body>
    <div class="center extrabold" style="font-size: ${scaled('13px', width)};">DPBN SUPER MARTS PVT LTD</div>
    <div class="center semibold" style="font-size: ${scaled('9px', width)};">Sattenapalli | Ph: 9493921579 | GSTIN: 37AALCD5749H1Z7</div>
    <div class="sep"></div>
    <table class="semibold" style="font-size: ${scaled('9px', width)};">
        <tr><td>Inv: ${invoiceNumber}</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td colspan="2" style="text-align: center;">Payment: ${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    <div class="sep"></div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        ${items.map(item => `
            <tr>
                <td class="semibold" style="font-size: ${scaled('10px', width)}; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3; max-width: ${width >= 80 ? '50mm' : '35mm'};">${item.productName} (${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''})</td>
            </tr>
            <tr class="semibold">
                <td>${item.quantity} x ${item.price.toFixed(2)}</td>
                <td style="text-align: right;" class="bold">${item.total.toFixed(2)}</td>
            </tr>
        `).join('')}
    </table>
    <div class="sep"></div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Subtotal:</td><td style="text-align: right;">${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax (Exclusive):</td><td style="text-align: right;">${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
        <tr class="extrabold" style="font-size: ${scaled('12px', width)};"><td>TOTAL:</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td></tr>
    </table>
    <div class="sep"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center extrabold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('2px', width)} 0;">GST BREAKDOWN</div>
    <div class="sep"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)};">TAX (incl)</div>
    <table class="semibold" style="font-size: ${scaled('8px', width)};">
        <tr><td>Taxable:</td><td style="text-align: right;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total:</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)};">TAX (excl)</div>
    <table class="semibold" style="font-size: ${scaled('8px', width)};">
        <tr><td>Taxable:</td><td style="text-align: right;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total:</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="font-size: ${scaled('10px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="sep"></div>
    ` : '';
    })()}
    
    <div class="center bold" style="font-size: ${scaled('10px', width)};">THANK YOU!</div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 4: ELEGANT BOUTIQUE
// ============================================
const elegantTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Georgia', serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.5;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 5mm;
            color: #2c2c2c;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .ornament { border-top: 3px double #2c2c2c; border-bottom: 3px double #2c2c2c; padding: ${scaledSpace('3px', width)} 0; margin: ${scaledSpace('4px', width)} 0; }
        .elegant-sep { border-top: 1px solid #888; margin: ${scaledSpace('4px', width)} 0; }
        table { width: 100%; border-collapse: collapse; margin: ${scaledSpace('3px', width)} 0; }
        td { padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)}; }
    </style>
</head>
<body>
    <div class="ornament center">
        <div class="extrabold" style="font-size: ${scaled('20px', width)}; letter-spacing: 2px;">DPBN</div>
        <div class="bold" style="font-size: ${scaled('16px', width)};">SUPER MARTS</div>
    </div>
    
    <div class="center semibold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('11px', width)}; font-style: italic;">
        Premium Quality Products<br/>
        23-9-26/1, Nirmala Nagar, Sattenapalli<br/>
        Phone: 9493921579
    </div>
    
    <div class="elegant-sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td style="font-style: italic;">Invoice Number</td><td style="text-align: right;" class="bold">${invoiceNumber}</td></tr>
        <tr><td style="font-style: italic;">Date & Time</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td style="font-style: italic;">Served By</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td style="font-style: italic;">Payment</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="elegant-sep"></div>
    
    <table style="font-size: ${scaled('11px', width)}">
        <thead>
            <tr class="bold" style="border-bottom: 2px solid #2c2c2c;">
                <td style="font-style: italic;">Item Description</td>
                <td style="text-align: center; width: ${width >= 80 ? '12mm' : '10mm'};">Qty</td>
                <td style="text-align: right; width: ${width >= 80 ? '15mm' : '12mm'};">Amount</td>
            </tr>
        </thead>
        <tbody>
            ${items.map(item => `
                <tr class="semibold" style="border-bottom: 1px dotted #ccc;">
                    <td style="padding: ${scaledSpace('3px', width)} 0;">
                        <div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div>
                        <span style="font-size: ${scaled('10px', width)}; color: #666;">${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''} @ Rs. ${item.price.toFixed(2)}</span>
                    </td>
                    <td style="text-align: center; vertical-align: top; padding-top: 3px;">${item.quantity}</td>
                    <td style="text-align: right; vertical-align: top; padding-top: 3px;" class="bold">${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="elegant-sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td style="font-style: italic;">Subtotal</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td style="font-style: italic;">Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td style="font-style: italic;">Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td style="font-style: italic;">Tax (Exclusive)</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="ornament">
        <table style="font-size: ${scaled('16px', width)};">
            <tr class="extrabold">
                <td>TOTAL</td>
                <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
            </tr>
        </table>
    </div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="elegant-sep"></div>
    <div class="center extrabold" style="font-size: ${scaled('13px', width)}; font-style: italic; margin: ${scaledSpace('3px', width)} 0;">GST BREAKDOWN</div>
    <div class="elegant-sep"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)}; font-style: italic; margin-top: ${scaledSpace('2px', width)};">Tax Inclusive</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td style="font-style: italic;">Taxable Value</td><td style="text-align: right;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td style="font-style: italic;">CGST</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td style="font-style: italic;">SGST</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px dotted #888;"><td style="font-style: italic;">Total GST</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)}; font-style: italic; margin-top: ${scaledSpace('3px', width)};">Tax Exclusive</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td style="font-style: italic;">Taxable Value</td><td style="text-align: right;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td style="font-style: italic;">CGST</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td style="font-style: italic;">SGST</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px dotted #888;"><td style="font-style: italic;">Total GST</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="elegant-sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('11px', width)} font-style: italic;">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="elegant-sep"></div>
    ` : '';
    })()}
    
    <div class="center bold" style="margin-top: ${scaledSpace('6px', width)}; font-size: ${scaled('12px', width)}; font-style: italic;">
        Thank you for your patronage
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 5: BOLD IMPACT
// ============================================
const boldTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: Arial, sans-serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.4;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .black-bar { background: #000; color: #fff; padding: ${scaledSpace('3px', width)}; margin: ${scaledSpace('3px', width)} 0; }
        .thick-sep { border-top: 3px solid #000; margin: ${scaledSpace('3px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)}; }
    </style>
</head>
<body>
    <div class="center extrabold" style="font-size: ${scaled('20px', width)}; border: 2px solid #000; padding: ${scaledSpace('3px', width)}; margin: ${scaledSpace('3px', width)} 0;">
        DPBN SUPER MARTS
    </div>
    
    <div class="center bold" style="margin: ${scaledSpace('3px', width)} 0; font-size: ${scaled('12px', width)};">
        PRIVATE LIMITED<br/>
        Phone: 9493921579
    </div>
    
    <div class="thick-sep"></div>
    
    <table class="bold" style="font-size: ${scaled('11px', width)};">
        <tr style="background: #f0f0f0;"><td>INVOICE</td><td style="text-align: right;">${invoiceNumber}</td></tr>
        <tr><td>DATE</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr style="background: #f0f0f0;"><td>CASHIER</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr style="background: #f0f0f0;"><td>PAYMENT</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="thick-sep"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <thead>
            <tr class="extrabold" style="border: 2px solid #000; border-bottom: 3px solid #000;">
                <td style="padding: ${scaledSpace('2px', width)};">ITEM</td>
                <td style="text-align: center; width: ${width >= 80 ? '12mm' : '10mm'}; padding: ${scaledSpace('2px', width)};">QTY</td>
                <td style="text-align: right; width: ${width >= 80 ? '15mm' : '12mm'}; padding: ${scaledSpace('2px', width)};">TOTAL</td>
            </tr>
        </thead>
        <tbody>
            ${items.map((item, idx) => `
                <tr style="border-bottom: 1px solid #ddd; ${idx % 2 === 0 ? 'background: #f9f9f9;' : ''}">
                    <td style="padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};">
                        <div class="bold" style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div>
                        <div class="semibold" style="font-size: ${scaled('10px', width)};">${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''} @ Rs. ${item.price.toFixed(2)}</div>
                    </td>
                    <td style="text-align: center; padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};" class="bold">${item.quantity}</td>
                    <td style="text-align: right; padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};" class="bold">${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="thick-sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>Subtotal</td><td style="text-align: right;" class="bold">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax (Exclusive)</td><td style="text-align: right;" class="bold">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div style="border: 3px solid #000; padding: ${scaledSpace('3px', width)}; margin: ${scaledSpace('3px', width)} 0;">
        <table style="font-size: ${scaled('18px', width)};" class="extrabold">
            <tr>
                <td>TOTAL</td>
                <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
            </tr>
        </table>
    </div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="thick-sep"></div>
    <div class="center extrabold" style="font-size: ${scaled('14px', width)}; margin: ${scaledSpace('2px', width)} 0;">GST BREAKDOWN</div>
    <div class="thick-sep"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)}; background: #f0f0f0; padding: ${scaledSpace('2px', width)};">TAX INCLUSIVE</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value</td><td style="text-align: right;" class="bold">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST</td><td style="text-align: right;" class="bold">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST</td><td style="text-align: right;" class="bold">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr style="border-top: 2px solid #000;"><td class="bold">TOTAL GST</td><td style="text-align: right;" class="bold">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)}; background: #f0f0f0; padding: ${scaledSpace('2px', width)}; margin-top: ${scaledSpace('3px', width)};">TAX EXCLUSIVE</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value</td><td style="text-align: right;" class="bold">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST</td><td style="text-align: right;" class="bold">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST</td><td style="text-align: right;" class="bold">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr style="border-top: 2px solid #000;"><td class="bold">TOTAL GST</td><td style="text-align: right;" class="bold">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="thick-sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center extrabold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('13px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="thick-sep"></div>
    ` : '';
    })()}
    
    <div class="center extrabold" style="margin-top: ${scaledSpace('6px', width)}; font-size: ${scaled('14px', width)};">
        THANK YOU!
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 6: RETAIL STANDARD
// ============================================
const retailTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Courier New', monospace;
            font-size: ${scaled('11px', width)};
            line-height: 1.3;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .sep { border-top: 1px solid #000; margin: ${scaledSpace('2px', width)} 0; }
        .dashed { border-top: 1px dashed #000; margin: ${scaledSpace('2px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('1px', width)} 0; }
    </style>
</head>
<body>
    <div class="center extrabold" style="font-size: ${scaled('16px', width)};">DPBN SUPER MARTS PRIVATE LIMITED</div>
    <div class="center semibold" style="margin-top: ${scaledSpace('2px', width)}; font-size: ${scaled('10px', width)};">
        23-9-26/1, Nirmala Nagar, Sattenapalli<br/>
        Palnadu, Andhra Pradesh - 522403<br/>
        Phone: 9493921579<br/>
        GSTIN: 37AALCD5749H1Z7
    </div>
    
    <div class="sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>INV NO: ${invoiceNumber}</td><td style="text-align: right;">DATE: ${formatDateTime(orderPlacedOn).split(' ')[0]}</td></tr>
        <tr><td colspan="2">TIME: ${formatDateTime(orderPlacedOn).split(' ')[1]}</td></tr>
        <tr><td colspan="2">CASHIER: ${salesPerson || 'Admin'}</td></tr>
        <tr><td colspan="2">PAYMENT: ${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="sep"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <thead>
            <tr class="bold" style="border-bottom: 1px solid #000; font-size: ${scaled('11px', width)};">
                <td>DESCRIPTION</td>
                <td style="text-align: center; width: ${width >= 80 ? '10mm' : '8mm'};">QTY</td>
                <td style="text-align: right; width: ${width >= 80 ? '12mm' : '10mm'};">PRICE</td>
                <td style="text-align: right; width: ${width >= 80 ? '12mm' : '10mm'};">TOTAL</td>
            </tr>
        </thead>
        <tbody>
            ${items.map(item => `
                <tr class="semibold">
                    <td><div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div><span style="font-size: ${scaled('9px', width)};">${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''}</span></td>
                    <td style="text-align: center;">${item.quantity}</td>
                    <td style="text-align: right;">${item.price.toFixed(2)}</td>
                    <td style="text-align: right;" class="bold">${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="dashed"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>SUBTOTAL:</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>COUPON:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>DELIVERY:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>TAX (EXCLUSIVE):</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="sep"></div>
    
    <table style="font-size: ${scaled('14px', width)};">
        <tr class="extrabold">
            <td>TOTAL:</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="sep"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center extrabold" style="font-size: ${scaled('12px', width)}; margin: ${scaledSpace('2px', width)} 0;">GST BREAKDOWN</div>
    <div class="dashed"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)};">TAX (INCLUSIVE)</div>
    <table class="semibold" style="font-size: ${scaled('9px', width)};">
        <tr><td>TAXABLE VALUE:</td><td style="text-align: left;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #000;"><td>TOTAL GST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)}; margin-top: ${scaledSpace('2px', width)};">TAX (EXCLUSIVE)</div>
    <table class="semibold" style="font-size: ${scaled('9px', width)};">
        <tr><td>TAXABLE VALUE:</td><td style="text-align: left;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #000;"><td>TOTAL GST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="dashed"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center extrabold" style="margin: ${scaledSpace('3px', width)} 0; font-size: ${scaled('12px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="dashed"></div>
    ` : '';
    })()}
    
    <div class="center" style="margin-top: ${scaledSpace('4px', width)};">
        <div class="bold" style="font-size: ${scaled('12px', width)};">THANK YOU FOR YOUR PURCHASE!</div>
        <div class="semibold" style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('2px', width)};">Please visit again</div>
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 7: PREMIUM LUXURY
// ============================================
const premiumTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Times New Roman', serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.6;
            width: ${width - 10}mm;
            margin: 0 auto;
            padding: 6mm;
            color: #1a1a1a;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .logo-area { border: 2px solid #1a1a1a; padding: ${scaledSpace('6px', width)}; margin-bottom: 6px; }
        .elegant-line { border-top: 1px solid #666; margin: ${scaledSpace('5px', width)} 0; }
        .double-line { border-top: 3px double #1a1a1a; margin: ${scaledSpace('5px', width)} 0; }
        table { width: 100%; border-collapse: collapse; margin: ${scaledSpace('4px', width)} 0; }
        td { padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)}; }
    </style>
</head>
<body>
    <div class="logo-area center">
        <div class="extrabold" style="font-size: ${scaled('22px', width)}; letter-spacing: 3px;">DPBN</div>
        <div class="bold" style="font-size: ${scaled('14px', width)}; margin-top: ${scaledSpace('2px', width)}; letter-spacing: 1px;">SUPER MARTS</div>
        <div class="semibold" style="font-size: ${scaled('10px', width)}; margin-top: ${scaledSpace('3px', width)}; font-style: italic;">Private Limited</div>
    </div>
    
    <div class="center semibold" style="font-size: ${scaled('10px', width)}; margin-bottom: 5px;">
        23-9-26/1, Nirmala Nagar, Near Railway Gate<br/>
        Sattenapalli, Palnadu District, AP - 522403<br/>
        Contact: 9493921579 | GSTIN: 37AALCD5749H1Z7
    </div>
    
    <div class="double-line"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td class="bold">Invoice Reference:</td><td style="text-align: right;">${invoiceNumber}</td></tr>
        <tr><td class="bold">Transaction Date:</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td class="bold">Sales Associate:</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td class="bold">Payment:</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="double-line"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <thead>
            <tr class="bold" style="border-bottom: 2px solid #1a1a1a;">
                <td style="padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};">PRODUCT DESCRIPTION</td>
                <td style="text-align: center; width: ${width >= 80 ? '12mm' : '10mm'}; padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};">QTY</td>
                <td style="text-align: right; width: ${width >= 80 ? '16mm' : '13mm'}; padding: ${scaledSpace('3px', width)} ${scaledSpace('2px', width)};">VALUE</td>
            </tr>
        </thead>
        <tbody>
            ${items.map(item => `
                <tr class="semibold" style="border-bottom: 1px solid #ddd;">
                    <td style="padding: ${scaledSpace('4px', width)} ${scaledSpace('2px', width)};">
                        <div class="bold" style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div>
                        <div style="font-size: ${scaled('10px', width)}; color: #666; margin-top: ${scaledSpace('1px', width)};">
                            ${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''} | Unit Price: Rs. ${item.price.toFixed(2)}
                        </div>
                    </td>
                    <td style="text-align: center; padding: ${scaledSpace('4px', width)} ${scaledSpace('2px', width)};">${item.quantity}</td>
                    <td style="text-align: right; padding: ${scaledSpace('4px', width)} ${scaledSpace('2px', width)};" class="bold">Rs. ${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="elegant-line"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>Subtotal Amount</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Applicable Tax (Exclusive)</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="double-line"></div>
    
    <table style="font-size: ${scaled('16px', width)};">
        <tr class="extrabold">
            <td>GRAND TOTAL</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="double-line"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center bold" style="font-size: ${scaled('11px', width)}; letter-spacing: 1px; margin: ${scaledSpace('4px', width)} 0;">GST BREAKDOWN</div>
    <div class="elegant-line"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)}; margin-top: ${scaledSpace('3px', width)};">Tax Inclusive Items</div>
    <table style="font-size: ${scaled('9px', width)};">
        <tr><td>Taxable Value</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>Central GST</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>State GST</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #666;"><td>Total GST</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('10px', width)}; margin-top: ${scaledSpace('4px', width)};">Tax Exclusive Items</div>
    <table style="font-size: ${scaled('9px', width)};">
        <tr><td>Taxable Value</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>Central GST</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>State GST</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #666;"><td>Total GST</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="double-line"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('12px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="elegant-line"></div>
    ` : '';
    })()}
    
    <div class="center" style="margin-top: ${scaledSpace('8px', width)}; font-size: ${scaled('11px', width)};">
        <div class="bold">Thank You for Your Business</div>
        <div style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('3px', width)}; font-style: italic;">
            We appreciate your patronage
        </div>
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 8: ECO FRIENDLY
// ============================================
const ecoTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: Arial, sans-serif;
            font-size: ${scaled('10px', width)};
            line-height: 1.3;
            width: ${width - 6}mm;
            margin: 0 auto;
            padding: 3mm;
            color: #333;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .light-line { border-top: 0.5px solid #ccc; margin: ${scaledSpace('2px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('1px', width)} 0; }
    </style>
</head>
<body>
    <div class="center extrabold" style="font-size: ${scaled('14px', width)};">DPBN SUPER MARTS PVT LTD</div>
    <div class="center semibold" style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('1px', width)};">
        Sattenapalli | 9493921579<br/>
        GSTIN: 37AALCD5749H1Z7
    </div>
    
    <div class="light-line"></div>
    
    <table class="semibold" style="font-size: ${scaled('9px', width)};">
        <tr><td>Inv: ${invoiceNumber}</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td colspan="2">By: ${salesPerson || 'Admin'}</td></tr>
        <tr><td colspan="2">Payment: ${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="light-line"></div>
    
    <table style="font-size: ${scaled('10px', width)};">
        ${items.map(item => `
            <tr class="semibold">
                <td style="font-size: ${scaled('10px', width)};">
                    <div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName} (${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''})</div>
                    <span style="font-size: ${scaled('9px', width)}; color: #666;">${item.quantity} x ${item.price.toFixed(2)}</span>
                </td>
                <td style="text-align: right; vertical-align: top; font-size: ${scaled('10px', width)};" class="bold">${item.total.toFixed(2)}</td>
            </tr>
        `).join('')}
    </table>
    
    <div class="light-line"></div>
    
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Subtotal:</td><td style="text-align: right;">${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax (Exclusive):</td><td style="text-align: right;">${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
        <tr class="extrabold" style="font-size: ${scaled('12px', width)};"><td>TOTAL:</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td></tr>
    </table>
    
    <div class="light-line"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center extrabold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('2px', width)} 0;">GST BREAKDOWN</div>
    <div class="light-line"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)};">Tax (incl)</div>
    <table class="semibold" style="font-size: ${scaled('8px', width)};">
        <tr><td>Taxable:</td><td style="text-align: right;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total:</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('2px', width)};">Tax (excl)</div>
    <table class="semibold" style="font-size: ${scaled('8px', width)};">
        <tr><td>Taxable:</td><td style="text-align: right;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold"><td>Total:</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="light-line"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('2px', width)} 0">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="light-line"></div>
    ` : '';
    })()}
    
    <div class="center bold" style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('2px', width)};">
        THANK YOU | SAVE PAPER 🌿
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 9: TECH MODERN
// ============================================
const techTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: ${scaled('10px', width)};
            line-height: 1.4;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .header-box { border: 2px solid #000; padding: ${scaledSpace('4px', width)}; margin-bottom: 4px; }
        .tech-sep { border-top: 2px solid #000; margin: ${scaledSpace('3px', width)} 0; }
        .dotted { border-top: 1px dotted #666; margin: ${scaledSpace('2px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)}; }
        .icon { display: inline-block; width: 12px; text-align: center; }
    </style>
</head>
<body>
    <div class="header-box center">
        <div class="extrabold" style="font-size: ${scaled('18px', width)};">[ DPBN SUPER MARTS ]</div>
        <div class="bold" style="font-size: ${scaled('11px', width)}; margin-top: ${scaledSpace('2px', width)};">PRIVATE LIMITED</div>
    </div>
    
    <div class="center semibold" style="font-size: ${scaled('10px', width)};">
        📍 Sattenapalli, AP | ☎ 9493921579<br/>
        🔖 GSTIN: 37AALCD5749H1Z7
    </div>
    
    <div class="tech-sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td class="bold">📄 INVOICE:</td><td style="text-align: right;">${invoiceNumber}</td></tr>
        <tr><td class="bold">📅 DATETIME:</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td class="bold">👤 OPERATOR:</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td class="bold">💳 PAYMENT:</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="tech-sep"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <thead>
            <tr class="extrabold" style="border-bottom: 2px solid #000; background: #f5f5f5;">
                <td style="padding: ${scaledSpace('2px', width)};">ITEM</td>
                <td style="text-align: center; width: ${width >= 80 ? '12mm' : '10mm'}; padding: ${scaledSpace('2px', width)};">QTY</td>
                <td style="text-align: right; width: ${width >= 80 ? '15mm' : '12mm'}; padding: ${scaledSpace('2px', width)};">AMT</td>
            </tr>
        </thead>
        <tbody>
            ${items.map((item, idx) => `
                <tr class="semibold" style="border-bottom: 1px dotted #999;">
                    <td style="padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)};">
                        <div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">🛒 <span class="bold">${item.productName}</span></div>
                        <span style="font-size: ${scaled('9px', width)};">   ${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''} @ ${item.price.toFixed(2)}</span>
                    </td>
                    <td style="text-align: center; padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)};">${item.quantity}</td>
                    <td style="text-align: right; padding: ${scaledSpace('2px', width)} ${scaledSpace('1px', width)};" class="bold">${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="dotted"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>💰 Subtotal:</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>🎟️ Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>🚚 Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>📊 Tax (Exclusive):</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="tech-sep"></div>
    
    <table style="font-size: ${scaled('15px', width)};">
        <tr class="extrabold">
            <td>💳 TOTAL:</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="tech-sep"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center bold" style="font-size: ${scaled('10px', width)}; margin: ${scaledSpace('2px', width)} 0;">📊 GST BREAKDOWN</div>
    <div class="dotted"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)};">🔸 Tax Inclusive</div>
    <table style="font-size: ${scaled('8px', width)};">
        <tr><td>   Taxable Value</td><td style="text-align: right;">${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>   CGST</td><td style="text-align: right;">${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>   SGST</td><td style="text-align: right;">${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px dotted #666;"><td>   Total GST</td><td style="text-align: right;">${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('9px', width)}; margin-top: ${scaledSpace('2px', width)};">🔸 Tax Exclusive</div>
    <table style="font-size: ${scaled('8px', width)};">
        <tr><td>   Taxable Value</td><td style="text-align: right;">${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>   CGST</td><td style="text-align: right;">${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>   SGST</td><td style="text-align: right;">${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px dotted #666;"><td>   Total GST</td><td style="text-align: right;">${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="tech-sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center bold" style="margin: ${scaledSpace('3px', width)} 0; font-size: ${scaled('11px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="dotted"></div>
    ` : '';
    })()}
    
    <div class="center bold" style="margin-top: ${scaledSpace('4px', width)}; font-size: ${scaled('10px', width)};">
        ✓ THANK YOU! ✓
    </div>
    <div class="center" style="font-size: ${scaled('8px', width)}; margin-top: ${scaledSpace('2px', width)};">
        Visit us again | Thank you for shopping with us!
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE 10: TRADITIONAL MARKET
// ============================================
const traditionalTemplate = (data, size) => {
    const { financial, items, invoiceNumber, orderPlacedOn, salesPerson, paymentInfo } = data;
    const width = parseInt(size) || 80;
    const gst = calculateGSTDetails(items);
    
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        @page { size: ${width}mm auto; margin: 0; }
        @media print { body { margin: 0; padding: 0; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Arial', sans-serif;
            font-size: ${scaled('11px', width)};
            line-height: 1.4;
            width: ${width - 8}mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: white;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .extrabold { font-weight: 900; }
        .semibold { font-weight: 600; }
        .sep { border-top: 1px solid #000; margin: ${scaledSpace('3px', width)} 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: ${scaledSpace('2px', width)} 0; }
    </style>
</head>
<body>
    <div class="center extrabold" style="font-size: ${scaled('17px', width)};">
        DPBN SUPER MARTS
    </div>
    <div class="center bold" style="font-size: ${scaled('12px', width)}; margin-top: ${scaledSpace('2px', width)};">
        PRIVATE LIMITED
    </div>
    <div class="center semibold" style="font-size: ${scaled('11px', width)}; margin-top: ${scaledSpace('3px', width)};">
        23-9-26/1, Nirmala Nagar<br/>
        Sattenapalli, Palnadu - 522403<br/>
        Phone: 9493921579
    </div>
    <div class="center bold" style="margin-top: ${scaledSpace('2px', width)}; font-size: ${scaled('11px', width)};">
        GSTIN: 37AALCD5749H1Z7
    </div>
    
    <div class="sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td class="bold">Bill No:</td><td style="text-align: right;">${invoiceNumber}</td></tr>
        <tr><td class="bold">Date:</td><td style="text-align: right;">${formatDateTime(orderPlacedOn)}</td></tr>
        <tr><td class="bold">Served By:</td><td style="text-align: right;">${salesPerson || 'Admin'}</td></tr>
        <tr><td class="bold">Payment:</td><td style="text-align: right;">${paymentInfo?.method || 'Cash'}</td></tr>
    </table>
    
    <div class="sep"></div>
    
    <table style="font-size: ${scaled('11px', width)};">
        <thead>
            <tr class="bold" style="border-bottom: 1px solid #000;">
                <td>ITEM</td>
                <td style="text-align: center; width: ${width >= 80 ? '12mm' : '10mm'};">QTY</td>
                <td style="text-align: right; width: ${width >= 80 ? '14mm' : '11mm'};">RATE</td>
                <td style="text-align: right; width: ${width >= 80 ? '14mm' : '11mm'};">AMOUNT</td>
            </tr>
        </thead>
        <tbody>
            ${items.map(item => `
                <tr class="semibold" style="border-bottom: 1px dotted #999;">
                    <td style="padding: ${scaledSpace('2px', width)} 0;">
                        <div style="overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.3;">${item.productName}</div>
                        <span style="font-size: ${scaled('10px', width)};">(${item.selectedOption?.weight ? (typeof item.selectedOption.weight === 'object' ? item.selectedOption.weight.weightLabel || `${item.selectedOption.weight.weightValue} ${item.selectedOption.weight.metricLabel}` : item.selectedOption.weight) : ''})</span>
                    </td>
                    <td style="text-align: center; padding: ${scaledSpace('2px', width)} 0;">${item.quantity}</td>
                    <td style="text-align: right; padding: ${scaledSpace('2px', width)} 0;">${item.price.toFixed(2)}</td>
                    <td style="text-align: right; padding: ${scaledSpace('2px', width)} 0;" class="bold">${item.total.toFixed(2)}</td>
                </tr>
            `).join('')}
        </tbody>
    </table>
    
    <div class="sep"></div>
    
    <table class="semibold" style="font-size: ${scaled('11px', width)};">
        <tr><td>Sub Total:</td><td style="text-align: right;" class="bold">Rs. ${formatIndianNumber(financial.subtotal)}</td></tr>
        ${(financial.couponDiscount && financial.couponDiscount > 0) ? `<tr><td>Coupon:</td><td style="text-align: right">-Rs. ${formatIndianNumber(financial.couponDiscount)}</td></tr>` : ''}
        ${(financial.offerDiscount && financial.offerDiscount > 0) ? `<tr style="background:#111;color:#fff;"><td style="padding:3px 2px;font-weight:900;">★ OFFER:</td><td style="text-align: right;padding:3px 2px;font-weight:900;">-Rs. ${formatIndianNumber(financial.offerDiscount)}</td></tr>${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : (financial.offerBillHighlight || financial.offerMessage) ? `${financial.offerBillHighlight ? `<tr><td colspan="2" style="text-align:center;font-weight:900;font-size:10px;padding:3px 0;border:1px dashed #000;">${financial.offerBillHighlight}</td></tr>` : ''}${financial.offerMessage ? `<tr><td colspan="2" style="font-size:9px;padding:2px 0;font-weight:bold;">${financial.offerMessage}</td></tr>` : ''}` : ''}
        ${(financial.deliveryCharges && financial.deliveryCharges > 0) ? `<tr><td>Delivery:</td><td style="text-align: right">Rs. ${formatIndianNumber(financial.deliveryCharges)}</td></tr>` : ''}
        ${financial.taxExclusive > 0 ? `<tr><td>Tax Amount (Exclusive):</td><td style="text-align: right;">Rs. ${formatIndianNumber(financial.taxExclusive)}</td></tr>` : ''}
    </table>
    
    <div class="sep"></div>
    
    <table style="font-size: ${scaled('15px', width)};">
        <tr class="extrabold">
            <td>GRAND TOTAL:</td>
            <td style="text-align: right;">Rs. ${formatIndianNumber(financial.finalAmount)}</td>
        </tr>
    </table>
    
    <div class="sep"></div>
    
    ${(gst.inclusiveDetails.totalGST > 0 || gst.exclusiveDetails.totalGST > 0) ? `
    <div class="center extrabold" style="font-size: ${scaled('13px', width)}; margin: ${scaledSpace('2px', width)} 0;">GST BREAKDOWN</div>
    <div class="sep"></div>
    
    ${gst.inclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)};">Tax Inclusive Items</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value:</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #666;"><td>Total GST:</td><td style="text-align: right;">Rs. ${gst.inclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    
    ${gst.exclusiveDetails.totalGST > 0 ? `
    <div class="bold" style="font-size: ${scaled('11px', width)}; margin-top: ${scaledSpace('3px', width)};">Tax Exclusive Items</div>
    <table class="semibold" style="font-size: ${scaled('10px', width)};">
        <tr><td>Taxable Value:</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.taxableValue.toFixed(2)}</td></tr>
        <tr><td>CGST:</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalCGST.toFixed(2)}</td></tr>
        <tr><td>SGST:</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalSGST.toFixed(2)}</td></tr>
        <tr class="bold" style="border-top: 1px solid #666;"><td>Total GST:</td><td style="text-align: right;">Rs. ${gst.exclusiveDetails.totalGST.toFixed(2)}</td></tr>
    </table>
    ` : ''}
    <div class="sep"></div>
    ` : ''}
    
    ${(() => {
        const savings = calculateTotalSavings(items);
        return savings > 0 ? `
    <div class="center extrabold" style="margin: ${scaledSpace('4px', width)} 0; font-size: ${scaled('11px', width)}">
        🎉 Hurray! You saved ₹${savings.toFixed(2)} 🎉
    </div>
    <div class="sep"></div>
    ` : '';
    })()}
    
    <div class="center" style="margin-top: ${scaledSpace('5px', width)};">
        <div class="bold" style="font-size: ${scaled('13px', width)};">THANK YOU</div>
        <div class="semibold" style="font-size: ${scaled('11px', width)}; margin-top: ${scaledSpace('2px', width)};">Please Visit Again</div>
    </div>
</body>
</html>
    `;
};

// ============================================
// TEMPLATE SELECTOR
// ============================================
const TEMPLATE_MAP = {
    classic: classicTemplate,
    modern: modernTemplate,
    compact: compactTemplate,
    elegant: elegantTemplate,
    bold: boldTemplate,
    retail: retailTemplate,
    premium: premiumTemplate,
    eco: ecoTemplate,
    tech: techTemplate,
    traditional: traditionalTemplate
};

// ============================================
// PUBLIC API
// ============================================
export const generateSampleReceipt = (templateId, size) => {
    const generator = TEMPLATE_MAP[templateId] || classicTemplate;
    return generator(SAMPLE_DATA, size);
};

export const generateReceipt = (orderDetails, templateId, size) => {
    const generator = TEMPLATE_MAP[templateId] || classicTemplate;
    return generator(orderDetails, size);
};





